import { Banner, Box, Stack, Strong, Text, TextField, type StackProps } from '@customerio/pluma-components/react';
import type { Ref } from 'react';
import type { PushPlatform } from '../types';
import { PLATFORM_LABELS } from '../utils/platforms';
import { CredentialFileField } from './credential-file-field';

type FcmSharedProps = {
  /** The file the user has picked but not yet saved. */
  selectedFileName: string | null;
  /** The file name stored alongside the saved credential. */
  savedFileName: string | null;
  /** Whether a new service account file may be chosen. */
  isEditable: boolean;
  /** Whether the fields and the file picker are locked. */
  isDisabled: boolean;
  /** Marks the credential file as what a failed save is complaining about. */
  isInvalid?: boolean;
  /** What a save found wrong with the file, if anything. */
  fileError?: string;
  ref?: Ref<HTMLDivElement>;
  onFileSelected: (file: File) => void;
} & StackProps;

export type FcmSettingsFieldsProps = FcmSharedProps & {
  variant: 'settings';
  /** Whether iOS can be turned on with the key already configured for Android. */
  hasAndroidKey: boolean;
};

export type FcmAppFieldsProps = FcmSharedProps & {
  variant: 'app';
  platform: PushPlatform;
  /** The Bundle ID on iOS, the app identifier on Android. */
  identifier: string;
  onIdentifierChange: (value: string) => void;
  /** The project id read from the file in play; shown read-only on Android. */
  projectId: string;
  /**
   * The project id of a credential already saved for the other platform. Set
   * only while this platform can reuse it; uploading a file takes over instead.
   */
  reuseProjectId: string | null;
  /** The platform whose in-progress upload this one is currently sharing. */
  sharedFileOwner: PushPlatform | null;
};

export type FcmCredentialFieldsProps = FcmSettingsFieldsProps | FcmAppFieldsProps;

/**
 * The service account file behind an FCM authorization, and — in an app — the
 * identifier the platform is known by.
 *
 * Fully controlled: the page that owns the values decides what a change means.
 */
export function FcmCredentialFields(props: FcmCredentialFieldsProps) {
  if (props.variant === 'settings') {
    return <FcmSettingsFields {...props} />;
  }
  return <FcmAppFields {...props} />;
}

function FcmSettingsFields({
  variant: _variant,
  hasAndroidKey,
  selectedFileName,
  savedFileName,
  isEditable,
  isDisabled,
  isInvalid = false,
  fileError,
  onFileSelected,
  ...boxProps
}: FcmSettingsFieldsProps) {
  return (
    <Box {...boxProps}>
      {hasAndroidKey ? (
        <Banner
          variant="success"
          mb="150"
          // Banner renders its description as a paragraph of its own, so this is
          // the sentence and nothing around it.
          description={
            <>
              <Strong>Good news!</Strong> You have an authorization key already set up for Android.
            </>
          }
        />
      ) : null}

      <CredentialFileField
        provider="fcm"
        selectedFileName={selectedFileName}
        savedFileName={savedFileName}
        isEditable={isEditable}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        error={fileError}
        onFileSelected={onFileSelected}
      />
    </Box>
  );
}

function FcmAppFields({
  variant: _variant,
  platform,
  identifier,
  onIdentifierChange,
  projectId,
  reuseProjectId,
  sharedFileOwner,
  selectedFileName,
  savedFileName,
  isEditable,
  isDisabled,
  isInvalid = false,
  fileError,
  onFileSelected,
  ...boxProps
}: FcmAppFieldsProps) {
  const isIos = platform === 'ios';
  // Reuse is the default while the sibling platform's saved credential is on
  // offer, until a file of this platform's own takes over.
  const isReusing = reuseProjectId != null && selectedFileName == null;
  // Only a standalone Android credential names itself after the app rather than
  // the bundle it ships in.
  const identifierField = identifierFieldFor(isIos || isReusing);

  return (
    <Stack gap="200" {...boxProps}>
      <TextField
        label={identifierField.label}
        id={identifierField.id}
        value={identifier}
        isDisabled={isDisabled}
        placeholder="com.sample.app"
        onInput={(event) => onIdentifierChange(event.target.value)}
      />

      <ProjectIdField projectId={shownProjectId(isIos, isReusing, projectId, reuseProjectId)} isReusing={isReusing} />

      <CredentialSourceNote isReusing={isReusing} sharedFileOwner={sharedFileOwner} />

      <CredentialFileField
        provider="fcm"
        selectedFileName={selectedFileName}
        savedFileName={savedFileName}
        isEditable={isEditable}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        error={fileError}
        onFileSelected={onFileSelected}
      />
    </Stack>
  );
}

/** How the platform names itself: by the bundle it ships in, or by the app. */
function identifierFieldFor(usesBundleId: boolean) {
  return usesBundleId
    ? { label: 'Bundle ID / App identifier', id: 'fcm_bundle_id' }
    : { label: 'App identifier', id: 'fcm_app_identifier' };
}

/**
 * The project id worth showing: the one being reused, or the one just read off
 * an upload. An iOS credential of its own is named by its bundle instead, so it
 * has none to show.
 */
function shownProjectId(
  isIos: boolean,
  isReusing: boolean,
  projectId: string,
  reuseProjectId: string | null,
): string | null {
  if (isReusing) {
    return reuseProjectId;
  }
  return isIos ? null : projectId;
}

type ProjectIdFieldProps = {
  projectId: string | null;
  isReusing: boolean;
};

/** The FCM project the credential belongs to. Never editable: it comes from the file. */
function ProjectIdField({ projectId, isReusing }: ProjectIdFieldProps) {
  if (projectId == null) {
    return null;
  }

  return (
    <TextField
      label="Project ID"
      id="fcm_project_id"
      value={projectId}
      isDisabled={true}
      placeholder={isReusing ? undefined : 'Read from the service account file'}
    />
  );
}

type CredentialSourceNoteProps = {
  isReusing: boolean;
  sharedFileOwner: PushPlatform | null;
};

/** Says where the credential in play comes from, when it is not this platform's own. */
function CredentialSourceNote({ isReusing, sharedFileOwner }: CredentialSourceNoteProps) {
  if (isReusing) {
    return (
      <Text color="subtle">
        This uses the FCM credential already configured for this app's Android platform. Upload a service account file
        below to use a separate credential instead.
      </Text>
    );
  }

  if (sharedFileOwner == null) {
    return null;
  }

  return (
    <Text color="subtle">
      This reuses the FCM credential you're setting up for the {PLATFORM_LABELS[sharedFileOwner]} platform. Upload a
      service account file to use a separate one instead.
    </Text>
  );
}
